import { reportError, watchErrors } from './errors.ts';
import { incursionLoot } from './campaign/incursion-loot.ts';
import { CINEMATICS, dueCinematic, playCinematic } from './campaign/cinematics.ts';
import { sagaBefore, sagaResult } from './campaign/saga-panel.ts';
import { betResult } from './campaign/bets-panel.ts';
import { sideResult } from './campaign/sides-panel.ts';
import { AscuasReveal, ascuasRevealDue } from './campaign/ascuas-reveal.ts';
import { AllyStrip } from './campaign/ally-strip.ts';
import { KeepChannel } from './campaign/keep-chat.ts';
import { registerWorker } from './push.ts';
import { incursionResult, snapshotBefore } from './campaign/incursion-result.ts';
import { cellarsEnd, cellarsOpening } from './campaign/cellars.ts';
import { furrowsEnd, furrowsOpening } from './campaign/furrows.ts';
import * as THREE from 'three';
import { ANIM, CROWD, FORM_FX } from '@somnhold/render';
import { BET, CELLARS, HERO_CLASSES, MAZE, PLAYABLE_CLASSES, SAGA_CHAPTERS, SAGA_SWITCH, INCURSION_OMENS, SIDES, type SideBattle, type SideId, type BetId, type CarryMode, type HeroClass, type IncursionOmen, type IncursionRisk, PLUNDER } from '@somnhold/content';
import { calendarMarea, cellarsOpen, eraOf, createBattle, dayNodes, replayStart, dayOmen, betsOf, sideOf, sideReplayAct, sagaOf, sagaRerun, type BattleState, type CampaignCommand, mazeRecord, mazeOpen, heroName } from '@somnhold/sim';
import { loadoutEditor } from './campaign/loadout.ts';
import { seasonNow } from './season.ts';
import { waveAt } from '@somnhold/protocol';
import { music } from './audio/music.ts';
import { audio, installAudioUnlock } from './audio/sfx.ts';
import { BastionScreen } from './campaign/bastion-screen.ts';
import { listLocal, LocalCampaign, localStatus, type CampaignService } from './campaign/service.ts';
import { api, ServerCampaign } from './campaign/server.ts';
import { acceptInviteModal, classPicker, createCampaignModal, fetchMe, inviteModal, modal, signInModal, signOut, toastGlobal, type Me } from './account.ts';
import { Backdrop } from './game/backdrop.ts';
import { BattleScreen } from './game/battle-screen.ts';
import { LiveLink } from './game/live.ts';
import { tutorialDone } from './game/coach.ts';
import { createPractice, type PracticeDemo, PRACTICE_BUILDS } from './game/practice.ts';
import { afterLandTide, createLandPoc, LAND_BUILDS, LAND_MAREAS, landBattle, type LandPoc } from './game/land-poc.ts';
import { watchForUpdates } from './update.ts';
import { getLang, reasonText, setLang, t } from './i18n.ts';
import { newsButton } from './news.ts';
import { saveSettings, settings } from './settings.ts';
import { clear, h } from './ui/dom.ts';
import { softGlNotice } from './ui/soft-gl.ts';
import { ICONS } from './ui/icons.ts';
import { thumb } from './ui/thumbs.ts';
import './ui/styles.css';

/** `?perf`, `?smooth=0`, `?anim=0`, `?predict=0`, `?cull=0` and `?fps=60` (to look into lag with a player) stay in the address as the game moves between screens. */
const DEBUG_PARAMS = ['perf', 'smooth', 'anim', 'predict', 'cull', 'fps', 'formfx'];
// `?anim=0`: the old 12 Hz poses instead of the animated clips (compare, or fall back).
if (new URLSearchParams(location.search).get('anim') === '0') ANIM.on = false;
// `?cull=0`: a big crowd drawn whole, out of the camera's sight too (compare).
if (new URLSearchParams(location.search).get('cull') === '0') CROWD.cull = false;
// `?formfx=0`: the unique pieces' forms without a look of their own, only their skills' (compare).
if (new URLSearchParams(location.search).get('formfx') === '0') FORM_FX.on = false;
const debugQuery = (lead: '?' | '&'): string => {
  const q = new URLSearchParams(location.search);
  const kept = DEBUG_PARAMS.filter((k) => q.has(k)).map((k) => (q.get(k) ? `${k}=${encodeURIComponent(q.get(k) as string)}` : k));
  return kept.length ? lead + kept.join('&') : '';
};

const app = document.getElementById('app') as HTMLElement;
installAudioUnlock(() => music.resume());
audio.setSfx(settings.sound, settings.sfxVol);
audio.setMusic(settings.music, settings.musicVol);

let renderer: THREE.WebGLRenderer | null = null;
const getRenderer = (): THREE.WebGLRenderer | null => {
  if (renderer) return renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: settings.quality === 'high', powerPreference: 'high-performance' });
  } catch (e) {
    reportError(e, 'gl-init');
    return null;
  }
  // The browser dropped the GPU context (low memory on a phone, a driver reset on a PC). Reloading right away
  // can find WebGL still blocked for the site, so wait for the context to come back and reload then; offer
  // the button only if it doesn't.
  // Whether the page was out of sight when the context went, and since when: Android drops a hidden tab's GPU
  // context to free memory, which says nothing about the game; a context lost in plain view does (prod: ~8 a day
  // on a 4 GB Adreno 618, most a few minutes after a fight, the report didn't say which kind).
  let hiddenAt = document.hidden ? performance.now() : -1;
  document.addEventListener('visibilitychange', () => (hiddenAt = document.hidden ? performance.now() : -1));
  renderer.domElement.addEventListener('webglcontextlost', (ev) => {
    ev.preventDefault();
    const now = performance.now();
    const mem = renderer?.info.memory;
    const s = (ms: number): number => Math.round(ms / 1000);
    reportError(new Error(`webglcontextlost hidden=${hiddenAt >= 0 ? `${s(now - hiddenAt)}s` : 'no'} up=${s(now)}s geo=${mem?.geometries ?? '?'} tex=${mem?.textures ?? '?'} prog=${renderer?.info.programs?.length ?? '?'}`), 'gl');
    if (document.querySelector('[data-test=gl-lost]')) return;
    const msg = h('span', {}, t('net.glLost'));
    const notice = h('div.update-notice', { role: 'alert', 'data-test': 'gl-lost' }, msg);
    document.body.append(notice);
    setTimeout(() => {
      msg.textContent = t('net.glLost.reload');
      notice.append(h('button.btn.small', { onclick: () => location.reload() }, t('update.reload')));
    }, 10_000);
  });
  renderer.domElement.addEventListener('webglcontextrestored', () => location.reload());
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  softGlNotice(renderer);
  return renderer;
};

let current: { destroy(): void } | null = null;
/** "Perdida" / "Ganada" next to a finished campaign's name in the menu. */
const endTag = (status: string): HTMLElement | string => (status === 'lost' || status === 'won' ? h(`span.end-tag.${status}`, {}, t(`menu.tag.${status}`)) : '');

const show = (el: HTMLElement, owner: { destroy(): void } | null = null): void => {
  current?.destroy();
  clear(app);
  app.append(el);
  current = owner;
};

/** No WebGL: usually the browser blocking it for a few seconds after a GPU reset, so keep trying and reload once it's back. */
const noGl = (): void => {
  const msg = h('p', {}, t('err.webgl'));
  let tries = 0;
  const retry = setInterval(() => {
    if (!document.contains(msg)) return clearInterval(retry);
    if (getRenderer()) {
      clearInterval(retry);
      location.reload();
    } else if (++tries >= 20) {
      clearInterval(retry);
      msg.textContent = t('err.webgl.gaveUp');
    }
  }, 3000);
  show(h('div.screen.menu-screen', {}, h('div.menu-card', {}, msg, h('button.btn', { onclick: () => void menu() }, t('menu.back')))));
};

// ---------- menu ----------

/** Under the title during a holiday season: what it is and what changes. */
const seasonRibbon = (): HTMLElement | null => {
  const id = seasonNow(Date.now());
  return id ? h(`div.season-ribbon.${id}`, {}, h('span.sr-ico', { html: ICONS.pumpkin }), h('div', {}, h('b', {}, t(`season.${id}`)), h('p', {}, t(`season.${id}.desc`)))) : null;
};

let me: Me | null | undefined;

/** The Google fonts come with `display=swap`: ask for them right away and hold the first menu until they're in
 *  (or 2.5 s pass), so it doesn't draw in the system fonts and jump an instant later. */
const fontsIn: Promise<unknown> = Promise.race([
  Promise.all(['1em "IM Fell English SC"', '1em "IM Fell English"', '1em "Alegreya Sans"', '700 1em "Alegreya Sans"'].map((f) => document.fonts.load(f))).catch(() => null),
  new Promise((r) => setTimeout(r, 2500)),
]);

const menu = async (): Promise<void> => {
  me = await fetchMe();
  await fontsIn;
  const locals = listLocal();
  // Every campaign in one list, online and local alike; the ongoing ones first, the finished ones folded away.
  type Row = { id: string; name: string; sub: string; status: string; online: boolean; players: number; mine?: boolean; test: string; open: () => void };
  const rows: Row[] = [];
  for (const c of me?.campaigns ?? []) {
    rows.push({
      id: c.id,
      players: c.players,
      mine: !!c.mine,
      name: c.name,
      sub: [t(`class.${c.heroClass}`), t(c.players === 1 ? 'campaigns.player' : 'campaigns.players', { n: c.players }), t('menu.online')].join(' · '),
      status: c.status,
      online: true,
      test: 'campaign',
      open: () => void openServer(c.id),
    });
  }
  for (const m of locals) {
    rows.push({
      id: m.id,
      players: 1,
      name: m.name,
      sub: [t('menu.localShort'), new Date(m.createdAt).toLocaleDateString(getLang())].join(' · '),
      status: localStatus(m.id)?.status ?? 'active',
      online: false,
      test: 'continue-local',
      open: () => openLocal(m.id),
    });
  }
  const row = (r: Row): HTMLElement => {
    const over = r.status === 'lost' || r.status === 'won';
    return h(
      `div.camp-row${over ? '.ended' : ''}`,
      {},
      h(
        `button.camp${over ? '.ended' : ''}`,
        { 'data-test': r.test, onclick: r.open },
        h('span.camp-ico', { html: r.online ? ICONS.keep : ICONS.practice }),
        h('span.camp-txt', {}, h('b', {}, r.name), h('span', {}, over ? t(`menu.ended.${r.status}`) : r.sub)),
        over ? endTag(r.status) : h('span.camp-go', { 'aria-hidden': 'true' }, '›'),
      ),
      h('button.camp-more', { 'aria-label': t('menu.manage', { name: r.name }), title: t('menu.manage', { name: r.name }), 'data-test': 'camp-more', onclick: () => manageCampaign(r) }, '⋯'),
    );
  };
  const active = rows.filter((r) => r.status !== 'lost' && r.status !== 'won');
  const ended = rows.filter((r) => r.status === 'lost' || r.status === 'won');
  const list = h('div.menu-list');
  list.append(h('div.menu-sec', {}, h('h2', {}, t('campaigns.title'))));
  if (!rows.length) list.append(h('p.menu-empty', {}, t('menu.noCampaigns')));
  for (const r of active) list.append(row(r));
  if (ended.length) list.append(h('details.camp-ended', {}, h('summary', {}, t('menu.endedList', { n: ended.length })), ...ended.map(row)));
  // The main way in: found a campaign with friends (or sign in first).
  if (me) {
    list.append(h('button.btn.menu-cta', { 'data-test': 'new-campaign', onclick: () => createCampaignModal(getRenderer(), me as Me, (id) => void openServer(id, true)) }, t('menu.found')));
  } else if (me === null) {
    list.append(h('button.btn.menu-cta', { 'data-test': 'sign-in', onclick: () => signInModal() }, t('menu.signIn')), h('p.menu-hint', {}, t('menu.signInWhy')));
  }
  list.append(
    h('div.menu-sec', {}, h('h2', {}, t('menu.solo'))),
    h(
      'div.solo-grid',
      {},
      h('button.solo', { 'data-test': 'new-local', onclick: () => newLocalModal() }, h('span', { html: ICONS.globe }), h('b', {}, t('menu.localCampaign')), h('span.small', {}, t('menu.localShortHint'))),
      h('button.solo', { 'data-test': 'practice', onclick: () => practice() }, h('span', { html: ICONS.practice }), h('b', {}, t('menu.practice')), h('span.small', {}, t('menu.practiceShort'))),
    ),
  );
  const foot = h(
    'div.menu-foot',
    {},
    h('button.link', {
      onclick: () => {
        setLang(getLang() === 'es' ? 'en' : 'es');
        void menu();
      },
    }, t('menu.language')),
    h('button.link', { onclick: () => openSettings() }, t('menu.settings')),
    newsButton('link.news-link'),
  );
  if (me) foot.append(h('button.link', { onclick: async () => { await signOut(); void menu(); } }, `${t('auth.logout')} (${me.user.email})`));
  void music.play('calm');
  const el = h('div.screen.menu-screen', {}, h('div.menu-veil'), h('div.menu-card', {}, h('div.eyebrow', {}, t('app.tagline')), h('h1.title', {}, t('app.title')), seasonRibbon(), h('hr.rule'), list, foot));
  const r = getRenderer();
  // Tear down the previous screen first: the backdrop shares the renderer's canvas and its teardown
  // would otherwise strip the canvas styling the new backdrop just set up (e.g. on a language switch).
  current?.destroy();
  current = null;
  const backdrop = r && new URLSearchParams(location.search).get('backdrop') !== '0' ? new Backdrop(r) : null;
  if (backdrop) el.prepend(backdrop.canvas);
  show(el, backdrop);
  backdrop?.start();
};

/**
 * Leaving a campaign: a test run is simply deleted; online, the hero stays on the wall as a guardian
 * for the others (and comes back if you rejoin), and the last one out deletes the campaign.
 */
const manageCampaign = (r: { id: string; name: string; status: string; online: boolean; players: number; mine?: boolean }): void => {
  const over = r.status === 'lost' || r.status === 'won';
  const kind = !r.online ? 'local' : r.players <= 1 ? 'alone' : over ? 'ended' : 'others';
  // Whoever founded an online campaign can rename it, for everyone.
  const naming = r.online && r.mine && !over;
  const nameIn = h('input.text-input', { type: 'text', maxlength: 40, value: r.name, 'aria-label': t('rename.label'), 'data-test': 'rename-input' }) as HTMLInputElement;
  const rename = async (): Promise<void> => {
    const name = nameIn.value.trim();
    if (!name || name === r.name) return;
    try {
      await api(`/api/campaigns/${r.id}/name`, { body: { name } });
    } catch {
      toastGlobal(t('net.error'));
      return;
    }
    back.remove();
    toastGlobal(t('rename.done', { name }));
    void menu();
  };
  const go = async (): Promise<void> => {
    if (!r.online) LocalCampaign.remove(r.id);
    else {
      try {
        await api(`/api/campaigns/${r.id}/leave`, { body: {} });
      } catch {
        toastGlobal(t('net.error'));
        return;
      }
    }
    back.remove();
    toastGlobal(t(`leave.done.${kind}`, { name: r.name }));
    void menu();
  };
  const leaveText = h('p.leave-text', {}, t(`leave.body.${kind}`));
  const body = naming
    ? h(
        'div',
        { style: 'display:grid;gap:12px' },
        h('div.field', {}, h('label', {}, t('rename.label')), h('div', { style: 'display:flex;gap:8px' }, nameIn, h('button.btn', { 'data-test': 'rename-save', onclick: () => void rename() }, t('rename.save')))),
        h('hr.rule'),
        leaveText,
      )
    : leaveText;
  nameIn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void rename();
  });
  const back = modal(naming ? t('menu.manage', { name: r.name }) : t(`leave.title.${kind}`, { name: r.name }), '', body, [
    h('button.btn.ghost', { onclick: () => back.remove() }, t('menu.back')),
    h('button.btn.danger', { 'data-test': 'leave-confirm', onclick: () => void go() }, t(`leave.go.${kind}`)),
  ]);
};

const openServer = async (id: string, justCreated = false): Promise<void> => {
  if (!me) return void menu();
  let svc: ServerCampaign;
  try {
    svc = await ServerCampaign.open(id, me.user.id);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  // A known reason says what went wrong (e.g. a hero still in a fight); otherwise, that the bastion moved on meanwhile.
  svc.onRebase = (n, why) => {
    const known = why?.map(reasonText).find((s) => s !== undefined);
    toastGlobal(known ?? t('net.rebased', { n }));
  };
  svc.onNetwork = (off) => {
    if (off) toastGlobal(t('net.offline'));
  };
  history.replaceState(null, '', `/?c=${encodeURIComponent(id)}${debugQuery('&')}`);
  bastion(svc);
  if (justCreated) void inviteModal(() => svc.invite());
};

const newLocalModal = (): void => {
  const r = getRenderer();
  let cls: HeroClass = 'warrior';
  const name = h('input.text-input', { type: 'text', maxlength: 24, value: getLang() === 'es' ? 'Guardiana' : 'Warden', 'aria-label': t('menu.yourName') }) as HTMLInputElement;
  const fast = h('input', { type: 'checkbox' }) as HTMLInputElement;
  fast.checked = true;
  // The bastion from scratch (docs/CONSTRUCCION.md): terraced land and the keep alone (unchecked: the classic ring).
  const land = h('input', { type: 'checkbox', 'data-test': 'local-land' }) as HTMLInputElement;
  land.checked = new URLSearchParams(location.search).has('tierra');
  const body = h(
    'div',
    { style: 'display:grid;gap:12px' },
    h('div.field', {}, h('label', {}, t('menu.yourName')), name),
    h('div.field', {}, h('span.lbl', {}, t('menu.pickClass')), classPicker(r, cls, (c) => (cls = c))),
    h('label.toggle', {}, h('span', {}, t('menu.fastPace')), fast),
    h('label.toggle', {}, h('span', {}, t('menu.landTrial')), land),
  );
  const back = modal(t('menu.newLocal'), t('menu.localCampaignHint'), body, [
    h('button.btn.ghost', { onclick: () => back.remove() }, t('menu.back')),
    h('button.btn', {
      'data-test': 'start-local',
      onclick: () => {
        const others = PLAYABLE_CLASSES.filter((c) => c !== cls);
        const c = LocalCampaign.create({
          name: name.value.trim() || 'Guardiana',
          heroClass: cls,
          companions: [
            { name: 'Tarsa', cls: others[0] as HeroClass },
            { name: 'Corvin', cls: others[1] as HeroClass },
          ],
          lang: getLang(),
          ...(fast.checked ? { dayMs: 10 * 60_000 } : {}),
          ...(land.checked ? { land: true } : {}),
        });
        back.remove();
        bastion(c);
      },
    }, t('menu.start')),
  ]);
};

const openSettings = (): void => {
  const row = (label: string, value: boolean, set: (v: boolean) => void): HTMLElement => {
    const input = h('input', { type: 'checkbox' }) as HTMLInputElement;
    input.checked = value;
    input.addEventListener('change', () => {
      set(input.checked);
      saveSettings();
    });
    return h('label.toggle', {}, h('span', {}, label), input);
  };
  const back = modal(
    t('menu.settings'),
    '',
    h(
      'div',
      {},
      row(t('settings.tiltShift'), settings.tiltShift, (v) => (settings.tiltShift = v)),
      row(t('settings.sound'), settings.sound, (v) => {
        settings.sound = v;
        audio.setSfx(v);
      }),
      row(t('settings.music'), settings.music, (v) => {
        settings.music = v;
        audio.setMusic(v);
      }),
      row(`${t('settings.quality')}: ${t('settings.quality.high')}`, settings.quality === 'high', (v) => (settings.quality = v ? 'high' : 'low')),
    ),
    [h('button.btn', { onclick: () => back.remove() }, t('hud.close'))],
  );
};

// ---------- campaign ----------

const openLocal = (id: string): void => {
  const c = LocalCampaign.load(id);
  if (!c) {
    LocalCampaign.remove(id);
    void menu();
    return;
  }
  bastion(c);
};

/** `story`: back from a saga battle, straight into the chronicle (and its next scene; `rerun`: of a chapter played again); no cinematic on top. */
const bastion = (svc: CampaignService, openIncursions = false, story: boolean | 'rerun' | { side: SideId } = false): void => {
  const r = getRenderer();
  if (!r) return noGl();
  // The second Marea opens the Ascuas: the group sees it once, before the bastion.
  if (ascuasRevealDue(svc.state)) {
    const rev: AscuasReveal = new AscuasReveal(r, svc.state, svc.me, (go) => bastion(svc, go));
    show(rev.el, rev);
    rev.start();
    return;
  }
  music.setEra(eraOf(svc.state));
  void music.play(svc.moment().phase === 'marea' ? 'marea' : 'calm');
  const screen = new BastionScreen({
    svc,
    renderer: r,
    onExit: () => {
      if (svc instanceof ServerCampaign) void svc.close();
      history.replaceState(null, '', `/${debugQuery('?')}`);
      void menu();
    },
    onDefend: (mode) => defend(svc, mode),
    onIncursion: (node, companions, risk, tier) => void incursion(svc, node, companions, risk, tier),
    onCrypt: (tier?: 1 | 2 | 3, ghosts?: string[]) => void crypt(svc, tier ?? 1, ghosts),
    onCellars: (scene?: boolean) => void expedition(svc, scene),
    onFurrows: (scene?: boolean) => void furrows(svc, scene),
    onSima: (start: number) => void sima(svc, start),
    // A finished chapter played again goes live online: whoever missed it, with friends who come along.
    onSaga: (rerun?: boolean, ghosts: string[] = []) => void (rerun && svc instanceof ServerCampaign ? rerunTogether(svc, `rerun=1${ghostsQuery(ghosts)}`, svc.me) : saga(svc, rerun, ghosts)),
    onBet: (id: BetId) => void bet(svc, id),
    onSide: (id: SideId, together?: boolean) => void (together && svc instanceof ServerCampaign ? sideTogether(svc, `side=${id}`, id) : side(svc, id)),
    onSideReplay: (id: SideId, chapter: number, together: boolean) => void (together && svc instanceof ServerCampaign ? rerunTogether(svc, `sidereplay=${id}&chapter=${chapter}`, svc.me) : sideReplay(svc, id, chapter)),
    ...(svc instanceof ServerCampaign
      ? {
          onTogether: (node: number, tier: 1 | 2 | 3) => void together(svc, `incursion=${node}${tier > 1 ? `&tier=${tier}` : ''}`, node),
          onSagaTogether: (ghosts: string[] = []) => void sagaTogether(svc, ghosts),
          onJoinRoom: (room: string): Promise<void> => {
            if (room === 'saga') return sagaTogether(svc);
            else if (room.startsWith('side-')) return sideTogether(svc, `room=${encodeURIComponent(room)}`, (room.split('-')[1] ?? '') as SideId);
            else if (room.startsWith('rerun-')) {
              const info = svc.presence()?.rooms.find((r) => r.id === room);
              return rerunTogether(svc, `room=${encodeURIComponent(room)}`, info?.leader ?? '', info?.node);
            }
            // A crypt can have a second room while one is under way ("crypt3-2"): the server picks it.
            else if (/^crypt(-|$)/.test(room)) return crypt(svc);
            else if (room.startsWith('crypt2')) return crypt(svc, 2);
            else if (room.startsWith('crypt3')) return crypt(svc, 3);
            // The Chasm: its room says where it starts ("sima-11", "sima-11-2").
            else if (room.startsWith('sima-')) return sima(svc, Number(room.split('-')[1] ?? '1') || 1);
            // The cellars: Hilde's scene the first time, never when going back into a run under way.
            else if (/^cellars(-|$)/.test(room)) return expedition(svc, svc.presence()?.rooms.find((r) => r.id === room)?.started ? false : undefined);
            // The Furrows: Brann's scene the first time, never when going back into a run under way.
            else if (/^maze(-|$)/.test(room)) return furrows(svc, svc.presence()?.rooms.find((r) => r.id === room)?.started ? false : undefined);
            else {
              const info = svc.presence()?.rooms.find((r) => r.id === room);
              return together(svc, `room=${encodeURIComponent(room)}`, info?.node ?? 0);
            }
          },
        }
      : {}),
    ...(svc instanceof ServerCampaign ? { onInvite: () => void inviteModal(() => svc.invite()), onReplay: (m: number, w: number, sec: number) => void replay(svc, m, w, sec) } : {}),
  });
  show(screen.el, screen);
  screen.start();
  if (openIncursions) screen.openMap();
  if (typeof story === 'object') return screen.continueSide(story.side);
  if (story) return screen.continueSaga(story === 'rerun');
  // A cinematic whose moment has come plays once over the bastion; the music comes back after it.
  const cine = dueCinematic(svc.state, svc.day());
  if (cine) playCinematic(cine, () => void music.play(svc.moment().phase === 'marea' ? 'marea' : 'calm'));
};


/**
 * After a fight: the screen already says how it ended; once `work` is done (sending the result, fetching
 * what it changed) what follows takes over. A failure says so and goes on, never leaving the end hanging.
 */
const afterFight = (screen: BattleScreen, work: Promise<unknown>, next: () => HTMLElement | void): void =>
  void work.catch(() => toastGlobal(t('net.error'))).then(() => screen.settle(next));

const defend = async (svc: CampaignService, mode: 'live' | 'lobby'): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  // Online every wave is one live room; offline you fight it alone with your companions' ghosts.
  if (svc instanceof ServerCampaign) return liveDefend(svc, r);
  const s = svc.state;
  const marea = s.mareaIndex;
  const wave = s.waveIndex;
  const sector = 0;
  void mode;
  let state: BattleState;
  try {
    state = await svc.prepareBattle(sector);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const screen: BattleScreen = new BattleScreen({
    state,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title: t('hud.wave', { n: wave + 1 }),
    subtitle: t('hud.marea', { n: marea + 1 }),
    onExit: () => {
      void svc.abandonBattle();
      bastion(svc);
    },
    onEnd: (b, log) => {
      afterFight(screen, svc.submitBattle({ marea, wave, sector, battle: b, log }), () => bastion(svc));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** Leaving a fight played alone before it ends: the server lets go of it, or the hero stays held in play (no swapping heroes) for half an hour. */
const quit = (svc: CampaignService): void => {
  void svc.abandonBattle();
  bastion(svc);
};

const incursion = async (svc: CampaignService, node: number, companions: string[], risk: IncursionRisk, tier: 1 | 2 | 3 = 1): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  let prep: { id: string; battle: BattleState };
  try {
    prep = await svc.prepareIncursion(node, companions, risk, tier);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const before = snapshotBefore(svc.state, svc.me);
  const kind = dayNodes(svc.state, svc.day()).find((n) => n.id === node)?.kind ?? 'quarry';
  const screen: BattleScreen = new BattleScreen({
    state: prep.battle,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: svc.kind === 'local',
    title: tier === 3 ? t('ilevel.title3', { place: t(`site.${node}`) }) : tier === 2 ? t('ilevel.title', { place: t(`site.${node}`) }) : risk === 'danger' ? t('inc.titleDanger', { place: t(`site.${node}`) }) : t(`site.${node}`),
    subtitle: `${tier > 1 ? `${t(`ilevel.${tier}.twist`)} ` : ''}${prep.battle.plunder ? t('plunder.twist', { n: PLUNDER.fullLoad }) : t(`inc.twist.${kind}`)} ${t('omen.line', { omen: t(`omen.${dayOmen(svc.state, svc.day())}`) })}`,
    centerpiece: `node:${kind}`,
    extraction: { kind, loot: incursionLoot(svc.state, svc.me, svc.day(), node, risk, tier) },
    onExit: () => quit(svc),
    onEnd: (b, log) => {
      // Say how it went, with the haul, before going back to the bastion.
      afterFight(screen, svc.submitIncursion(prep.id, b, log), () => incursionResult(svc.state, svc.me, before, { won: b.result === 'won', place: t(`inc.${kind}`), onBack: () => bastion(svc) }));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** What the saga's next battle act is called, and the line under it (`at`: a given chapter and act, e.g. a friend's replay). */
const sagaTitles = (svc: CampaignService, rerun = false, given?: { chapter: number; act: number } | null): { title: string; subtitle: string } => {
  const sg = sagaOf(svc.state);
  const at = given ?? (rerun ? sagaRerun(svc.state, svc.me) : sg);
  const ci = at?.chapter ?? sg.chapter;
  const ch = SAGA_CHAPTERS[ci];
  const act = ch && at ? ch.acts[at.act] : undefined;
  if (!ch || !act || act.kind !== 'battle') return { title: t('saga.title'), subtitle: '' };
  return { title: t(`saga.${ch.id}.${act.id}.title`), subtitle: `${t('saga.chapter', { n: ci + 1, of: SAGA_CHAPTERS.length })} · ${t(`saga.${ch.id}.${act.id}.goal`)}` };
};

/** A saga battle act, alone (online the server re-plays it from the log); `rerun`: of a finished chapter played again. */
const saga = async (svc: CampaignService, rerun = false, ghosts: string[] = []): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  let prep: { id: string; battle: BattleState };
  try {
    prep = await svc.prepareSaga(rerun, undefined, undefined, ghosts);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const before = sagaBefore(svc);
  const { title, subtitle } = sagaTitles(svc, rerun);
  void music.play(prep.battle.rules?.bossWins ? 'boss' : 'marea');
  const screen: BattleScreen = new BattleScreen({
    state: prep.battle,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: svc.kind === 'local',
    title,
    subtitle,
    onExit: () => quit(svc),
    onEnd: (b, log) => {
      afterFight(screen, svc.submitSaga(prep.id, b, log), () => sagaResult(svc, before, b.result === 'won', title, () => bastion(svc, false, rerun ? (b.result === 'won' ? 'rerun' : false) : true), undefined, rerun));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** One of Brann and Hilde's bets, alone (online the server re-plays it from the log). */
const bet = async (svc: CampaignService, id: BetId): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  let prep: { id: string; battle: BattleState };
  try {
    prep = await svc.prepareSaga(false, id);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const first = !betsOf(svc.state).won.includes(id);
  const title = t(`bet.${id}.title`);
  void music.play('marea');
  const screen: BattleScreen = new BattleScreen({
    state: prep.battle,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: svc.kind === 'local',
    title,
    subtitle: t(`bet.${id}.rule`, { n: BET.alone.max }),
    onExit: () => quit(svc),
    onEnd: (b, log) => {
      const won = b.result === 'won';
      const race = b.rules?.race;
      const raced = race ? { ticks: Math.min(b.waveT, race.limit), brann: race.brann, out: b.waveT >= race.limit } : undefined;
      afterFight(screen, svc.submitSaga(prep.id, b, log), () => betResult(id, won, first && won, title, () => bastion(svc, false, true), b.rules?.burst ? (b.burst?.best ?? 0) : undefined, raced));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** A side saga's next fight, alone (online the server re-plays it from the log). */
/** A finished side chapter's last fight played again (La Orilla 5): its piece and the dragon's seal for whoever lacks them. */
const sideReplay = async (svc: CampaignService, id: SideId, chapter: number): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  const ch = SIDES.find((d) => d.id === id)?.chapters[chapter];
  const act = ch?.acts[sideReplayAct(id, chapter)];
  if (!ch || act?.kind !== 'battle') return;
  let prep: { id: string; battle: BattleState };
  try {
    prep = await svc.prepareSaga(false, undefined, id, [], chapter);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const title = t(`side.${id}.${ch.id}.${act.id}.title`);
  void music.play('boss');
  const screen: BattleScreen = new BattleScreen({
    state: prep.battle,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: svc.kind === 'local',
    title,
    subtitle: t(`side.${id}.${ch.id}.${act.id}.goal`),
    onExit: () => quit(svc),
    onEnd: (b, log) => {
      afterFight(screen, svc.submitSaga(prep.id, b, log), () => sideResult(svc.state, id, b.result === 'won', title, [], false, () => bastion(svc, false, { side: id })));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** How a side saga's fight ended (alone or live): what it found, how the herd or the hunt went, and back to the bastion. */
const sideEnd = (svc: CampaignService, id: SideId, act: SideBattle, b: BattleState, before: number, actBefore: number, title: string): HTMLElement => {
  const won = b.result === 'won';
  // What the fight found, and what a win leaves on the board by itself.
  const found = [...(b.clues?.found ?? []).map((c) => c.id), ...(won ? (act.winClues ?? []) : [])];
  const saved = b.rules?.rescue ? t('side.result.saved', { n: b.rescue?.saved ?? 0, of: b.rules.rescue.people }) : b.rules?.herd ? t('side.result.penned', { n: b.herd?.penned ?? 0, of: b.rules.herd.boars }) : b.rules?.fair ? t('side.result.fair', { n: (b.fair?.wares ?? []).filter((w) => w.at === 'stall').length, of: b.fair?.wares.length ?? 0 }) : undefined;
  // The hunt: an outing that didn't finish the Uro still left its wounds (and another's may have finished it).
  const after = sideOf(svc.state, id);
  const hunt = b.rules?.hunt;
  if (hunt) {
    const fell = after.chapter !== before || after.act !== actBefore;
    const took = (b.hunt?.wounds ?? []).reduce((a, w) => a + w.dmg, 0);
    const left = fell ? 0 : Math.ceil((100 * (after.hunt?.hp ?? hunt.hp)) / Math.max(1, after.hunt?.max ?? hunt.max));
    const tally = t('side.result.hunt', { took: Math.round((100 * took) / Math.max(1, hunt.max)), left });
    return sideResult(svc.state, id, fell, title, found, after.chapter > before, () => bastion(svc, false, { side: id }), fell ? undefined : 'side.result.huntOut', tally, !fell);
  }
  return sideResult(svc.state, id, won, title, found, after.chapter > before, () => bastion(svc, false, { side: id }), !won && b.crowd?.escaped.length ? 'side.result.escaped' : !won && b.rescue?.lost ? 'side.result.rescueLost' : !won && b.rules?.herd ? 'side.result.herdLost' : undefined, saved);
};

const side = async (svc: CampaignService, id: SideId): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  const st = sideOf(svc.state, id);
  const ch = SIDES.find((d) => d.id === id)?.chapters[st.chapter];
  const act = ch?.acts[st.act];
  if (!ch || act?.kind !== 'battle') return;
  let prep: { id: string; battle: BattleState };
  try {
    prep = await svc.prepareSaga(false, undefined, id);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const before = st.chapter;
  const actBefore = st.act;
  const title = t(`side.${id}.${ch.id}.${act.id}.title`);
  void music.play('marea');
  const screen: BattleScreen = new BattleScreen({
    state: prep.battle,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: svc.kind === 'local',
    title,
    subtitle: t(`side.${id}.${ch.id}.${act.id}.goal`),
    onExit: () => quit(svc),
    onEnd: (b, log) => {
      afterFight(screen, svc.submitSaga(prep.id, b, log), () => sideEnd(svc, id, act, b, before, actBefore, title));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** The saga's next battle act with friends, live: whoever is around gathers and they go together. */
/** The dragon's spectres, for a live room's address. */
const ghostsQuery = (ghosts: string[]): string => (ghosts.length ? `&ghosts=${ghosts.map(encodeURIComponent).join(',')}` : '');

const sagaTogether = async (svc: ServerCampaign, ghosts: string[] = []): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const before = sagaBefore(svc);
  const { title, subtitle } = sagaTitles(svc);
  const link = new LiveLink(svc.id, `saga=1${ghostsQuery(ghosts)}`, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('party.full') : t('net.error'));
    return;
  }
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title,
    subtitle,
    net: link,
    liveInfo: { carry: carryFor(svc, 'saga'), due: 0, now: () => svc.now(), names, allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      afterFight(screen, svc.refresh(), () => sagaResult(svc, before, b.result === 'won', title, () => bastion(svc, false, true)));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * A side saga's next fight, live: whoever opened the room and the friends who join (the fights scale with how many
 * play). Winning moves the saga on for the group, as alone; the clues found go on the board either way.
 */
const sideTogether = async (svc: ServerCampaign, query: string, id: SideId): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const st = sideOf(svc.state, id);
  const ch = SIDES.find((d) => d.id === id)?.chapters[st.chapter];
  const act = ch?.acts[st.act];
  if (!ch || act?.kind !== 'battle') return;
  const before = st.chapter;
  const actBefore = st.act;
  const title = t(`side.${id}.${ch.id}.${act.id}.title`);
  const link = new LiveLink(svc.id, query, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('party.full') : t('net.error'));
    return;
  }
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title,
    subtitle: t(`side.${id}.${ch.id}.${act.id}.goal`),
    net: link,
    liveInfo: { carry: carryFor(svc, 'saga'), due: 0, now: () => svc.now(), names, allies: alliesFor(svc), keep: keepChatFor(svc), minParty: 1 },
    onExit: () => {
      link.close();
      bastion(svc, false, { side: id });
    },
    onEnd: (b) => {
      link.close();
      afterFight(screen, svc.refresh(), () => sideEnd(svc, id, act, b, before, actBefore, title));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * A finished chapter's act played again, live (docs/QUESTS.md): `owner` is the hero who missed it and opened the
 * room; friends join to help. Winning moves the owner's replay on (its next scene opens for them); a loss goes
 * back to the bastion, to try again or not.
 */
const rerunTogether = async (svc: ServerCampaign, query: string, owner: string, node?: number): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const before = sagaBefore(svc);
  const mine = owner === svc.me;
  const at = node !== undefined ? { chapter: Math.floor(node / 64), act: node % 64 } : null;
  const { title, subtitle } = sagaTitles(svc, mine, mine ? null : at);
  const link = new LiveLink(svc.id, query, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('party.full') : t('net.error'));
    return;
  }
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title,
    subtitle,
    net: link,
    liveInfo: { carry: carryFor(svc, 'saga'), due: 0, now: () => svc.now(), names, allies: alliesFor(svc), keep: keepChatFor(svc), minParty: 1 },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      const won = b.result === 'won';
      afterFight(screen, svc.refresh(), () => sagaResult(svc, before, won, title, () => bastion(svc, false, mine && won ? 'rerun' : false), undefined, true));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * The crypt: online, a live room where 2 to 4 gather and go down together; in a local campaign, alone.
 * At the end, the same card as an incursion tells what came back.
 */
/** The group strip for a live room's waiting room (who is around and what they're up to). */
/** "What to carry" for a waiting room: the hero's preparations, saved at once; the room rebuilds its fight with them. */
const carryFor = (svc: CampaignService, mode: CarryMode): HTMLElement => {
  const box = h('div.lobby-carry-box');
  const cmd = async (c: CampaignCommand): Promise<boolean> => {
    const r = await svc.command(c);
    if (!r.ok) toastGlobal(reasonText(r.reason) ?? t('reason.generic'));
    return r.ok;
  };
  const render = (): void => {
    const p = svc.state.players.find((x) => x.id === svc.me);
    if (p) box.replaceChildren(loadoutEditor(svc.state, p, cmd, render, mode));
  };
  render();
  return box;
};

const alliesFor = (svc: ServerCampaign): { el: HTMLElement; render(): void; poll(): Promise<unknown>; skip(pids: string[]): void } => {
  const strip = new AllyStrip(svc, (text) => toastGlobal(text));
  return {
    el: strip.el,
    render: () => strip.render(),
    poll: () => svc.pollPresence(),
    skip: (pids: string[]) => {
      strip.skip = new Set(pids);
      strip.render();
    },
  };
};

/** The keep's chat inside a live room: what was said in the bastion so far, then fed by each presence poll. */
const keepChatFor = (svc: ServerCampaign): { channel: KeepChannel; poll(): Promise<void> } => {
  const channel = new KeepChannel((text) => svc.keepSay(text));
  const said = svc.presence()?.chat;
  if (said) channel.prime(said);
  return {
    channel,
    poll: async () => {
      const p = await svc.pollPresence();
      if (p?.chat) channel.take(p.chat);
    },
  };
};

/** A crypt: alone, with friends who join live, and the absent friends picked (`ghosts`) as spectres. */
const crypt = async (svc: CampaignService, tier: 1 | 2 | 3 = 1, ghosts: string[] = []): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  const before = snapshotBefore(svc.state, svc.me);
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  // Each crypt's chest is once a day per hero (the crypt in embers has its own): say who already took it.
  const claimed = (): string[] => svc.state.players.filter((p) => (tier === 3 ? p.dungeonDay3 : tier === 2 ? p.dungeonDay2 : p.dungeonDay) === svc.day()).map((p) => p.id);
  const claimedBefore = claimed().includes(svc.me);
  const title = tier === 3 ? t('crypt3.title') : tier === 2 ? t('crypt2.title') : t('dungeon.title');
  const subtitle = tier === 3 ? t('crypt3.sub') : tier === 2 ? t('crypt2.sub') : t('dungeon.sub');
  const done = (won: boolean, screen: BattleScreen, work: Promise<unknown>): void =>
    afterFight(screen, work, () => incursionResult(svc.state, svc.me, before, { won, place: title, crypt: true, claimedBefore, onBack: () => bastion(svc) }));
  if (!(svc instanceof ServerCampaign)) {
    const screen: BattleScreen = new BattleScreen({
      state: svc.prepareDungeon(tier, [], ghosts),
      me: svc.me,
      renderer: r,
      sandbox: false,
      allowPause: true,
      title,
      subtitle,
      liveInfo: { carry: carryFor(svc, 'crypt'), due: 0, now: () => svc.now(), names, claimed },
      onExit: () => bastion(svc),
      onEnd: (b) => done(b.result === 'won', screen, svc.submitDungeon(b, tier)),
    });
    show(screen.el, screen);
    screen.start();
    return;
  }
  await svc.flush();
  // The spectres come with whoever opens the gathering; one already open is joined as it is.
  const link = new LiveLink(svc.id, `dungeon=${tier}${ghosts.length ? `&ghosts=${ghosts.map(encodeURIComponent).join(',')}` : ''}`, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('dungeon.busy') : t('net.error'));
    return;
  }
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title,
    subtitle,
    net: link,
    liveInfo: { carry: carryFor(svc, 'crypt'), due: 0, now: () => svc.now(), names, claimed, allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      done(b.result === 'won', screen, svc.refresh());
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * The Chasm (docs/SIMA.md): alone in a local campaign; online a live room for 1 to 8 from the chosen floor. At the end,
 * how deep the run went, what it brought and whether the group's mark moved.
 */
const sima = async (svc: CampaignService, start: number): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  const before = snapshotBefore(svc.state, svc.me);
  const markBefore = svc.state.sima?.best ?? 0;
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const claimed = (): string[] => svc.state.players.filter((p) => p.simaDay === svc.day()).map((p) => p.id);
  const claimedBefore = claimed().includes(svc.me);
  const title = t('sima.title');
  const subtitle = t('sima.label');
  const done = (b: BattleState, screen: BattleScreen, work: Promise<unknown>): void => {
    const sm = b.dungeon?.sima;
    const won = b.result === 'won';
    const deepest = sm && sm.done > 0 ? sm.start + sm.done - 1 : 0;
    afterFight(screen, work, () => {
      const mark = svc.state.sima?.best ?? markBefore;
      const extra = h('p.sima-res', { 'data-test': 'sima-res' }, mark > markBefore ? t('sima.resRecord', { n: mark }) : t('sima.resMark', { n: mark }));
      return incursionResult(svc.state, svc.me, before, {
        won,
        place: title,
        crypt: true,
        claimedBefore,
        heading: won ? { title: t('sima.resWon', { n: deepest || sm?.floor || start }), sub: t('sima.resWonSub', { k: sm?.done ?? 0, from: sm?.start ?? start }) } : { title: t('sima.resLost', { n: sm?.floor ?? start }), sub: t('sima.resLostSub') },
        extra: [extra],
        onBack: () => bastion(svc),
      });
    });
  };
  if (!(svc instanceof ServerCampaign)) {
    const screen: BattleScreen = new BattleScreen({
      state: svc.prepareSima(start),
      me: svc.me,
      renderer: r,
      sandbox: false,
      allowPause: true,
      title,
      subtitle,
      liveInfo: { carry: carryFor(svc, 'crypt'), due: 0, now: () => svc.now(), names, claimed },
      onExit: () => bastion(svc),
      onEnd: (b) => done(b, screen, svc.submitSima(b)),
    });
    show(screen.el, screen);
    screen.start();
    return;
  }
  await svc.flush();
  const link = new LiveLink(svc.id, `sima=${start}`, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('dungeon.busy') : t('net.error'));
    return;
  }
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title,
    subtitle,
    net: link,
    liveInfo: { carry: carryFor(svc, 'crypt'), due: 0, now: () => svc.now(), names, claimed, allies: alliesFor(svc), keep: keepChatFor(svc), minParty: 1 },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      done(b, screen, svc.refresh());
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * The garrison cellars (docs/EXPEDICIONES.md): Hilde's scene by the well, then a live room where three or four
 * gather and go down together. Online only: in a local campaign the card says why it can't be played.
 */
const expedition = async (svc: CampaignService, scene?: boolean): Promise<void> => {
  if (!(svc instanceof ServerCampaign)) {
    toastGlobal(t('exp.onlineOnly', { min: CELLARS.minHeroes, max: CELLARS.maxHeroes }));
    return;
  }
  if (!cellarsOpen(svc.state)) {
    toastGlobal(t('exp.closed'));
    return;
  }
  // Hilde's scene the first time each player goes down in a campaign (the card can show it again).
  const seenKey = `somnhold.cellars.scene.${svc.state.id}`;
  let seen = false;
  try {
    seen = localStorage.getItem(seenKey) === '1';
  } catch {
    /* private mode: always */
  }
  if (scene ?? !seen) {
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* fine */
    }
    document.body.append(cellarsOpening(() => void expedition(svc, false), () => undefined));
    return;
  }
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const before = snapshotBefore(svc.state, svc.me);
  // The day's chest is once per hero: who already opened it (the lobby tags them, the end card says it).
  const claimed = (): string[] => svc.state.players.filter((p) => p.expeditionDay === svc.day()).map((p) => p.id);
  const chestBefore = new Set(claimed());
  const link = new LiveLink(svc.id, 'expedition=cellars', svc.me);
  try {
    await link.connect();
  } catch (e) {
    const why = (e as Error).message;
    toastGlobal(why === 'full' ? t('exp.full') : why === 'refused' ? t('exp.closed') : t('net.error'));
    return;
  }
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title: t('exp.title'),
    subtitle: t('exp.sub'),
    net: link,
    liveInfo: { carry: carryFor(svc, 'expedition'), due: 0, now: () => svc.now(), names, claimed, allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      afterFight(screen, svc.refresh(), () => cellarsEnd(svc, b, before, chestBefore, () => bastion(svc)));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * The Furrows (docs/LABERINTOS.md): Brann's scene in the barracks, then a live room where one to four go down into the
 * week's labyrinth. Online only (the server runs every run and keeps the group's map).
 */
const furrows = async (svc: CampaignService, scene?: boolean): Promise<void> => {
  if (!(svc instanceof ServerCampaign)) {
    toastGlobal(t('maze.onlineOnly'));
    return;
  }
  if (!mazeOpen(svc.state)) {
    toastGlobal(t('maze.closed', { n: MAZE.minGroup }));
    return;
  }
  // Brann's scene the first time each player goes down in a campaign (the card can show it again).
  const seenKey = `somnhold.maze.scene.${svc.state.id}`;
  let seen = false;
  try {
    seen = localStorage.getItem(seenKey) === '1';
  } catch {
    /* private mode: always */
  }
  if (scene ?? !seen) {
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* fine */
    }
    document.body.append(furrowsOpening(() => void furrows(svc, false), () => undefined));
    return;
  }
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const before = snapshotBefore(svc.state, svc.me);
  const paidBefore = new Set(mazeRecord(svc.state, svc.day()).paid);
  const link = new LiveLink(svc.id, 'maze=1', svc.me);
  try {
    await link.connect();
  } catch (e) {
    const why = (e as Error).message;
    toastGlobal(why === 'full' ? t('maze.full') : why === 'refused' ? t('maze.closed', { n: MAZE.minGroup }) : t('net.error'));
    return;
  }
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title: t('maze.title'),
    subtitle: t('maze.sub'),
    net: link,
    liveInfo: { due: 0, now: () => svc.now(), names, claimed: () => mazeRecord(svc.state, svc.day()).paid, allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      afterFight(screen, svc.refresh(), () => furrowsEnd(svc, b, before, paidBefore, () => bastion(svc)));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/**
 * An incursion together, live: the leader opens a room (friends online see a card to join), everyone
 * gathers at the clearing and they set out once at least two are in. A dropped friend's ghost goes on.
 */
const together = async (svc: ServerCampaign, query: string, node: number): Promise<void> => {
  const r = getRenderer();
  if (!r) return noGl();
  await svc.flush();
  const before = snapshotBefore(svc.state, svc.me);
  const link = new LiveLink(svc.id, query, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('party.full') : t('net.error'));
    return;
  }
  const kind = dayNodes(svc.state, svc.day()).find((n) => n.id === node)?.kind ?? 'quarry';
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title: t(`site.${node}`),
    subtitle: `${(link.client.state as BattleState).plunder ? t('plunder.twist', { n: PLUNDER.fullLoad }) : t(`inc.twist.${kind}`)} ${t('omen.line', { omen: t(`omen.${dayOmen(svc.state, svc.day())}`) })}`,
    centerpiece: `node:${kind}`,
    extraction: { kind, loot: incursionLoot(svc.state, svc.me, svc.day(), node) },
    net: link,
    liveInfo: { carry: carryFor(svc, 'incursion'), due: 0, now: () => svc.now(), names, allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: (b) => {
      link.close();
      afterFight(screen, svc.refresh(), () => incursionResult(svc.state, svc.me, before, { won: b.result === 'won', place: t(`site.${node}`), onBack: () => bastion(svc) }));
    },
  });
  show(screen.el, screen);
  screen.start();
};

/** The wave live, in lockstep with whoever else is there (everyone else's hero fights as a ghost). */
const liveDefend = async (svc: ServerCampaign, r: THREE.WebGLRenderer): Promise<void> => {
  await svc.flush();
  const s = svc.state;
  const marea = s.mareaIndex;
  const wave = s.waveIndex;
  const link = new LiveLink(svc.id, 0, svc.me);
  try {
    await link.connect();
  } catch (e) {
    toastGlobal((e as Error).message === 'full' ? t('live.full') : t('net.error'));
    return;
  }
  const screen: BattleScreen = new BattleScreen({
    state: link.client.state as BattleState,
    me: svc.me,
    renderer: r,
    sandbox: false,
    allowPause: false,
    title: t('hud.wave', { n: wave + 1 }),
    subtitle: t('hud.marea', { n: marea + 1 }),
    net: link,
    liveInfo: { carry: carryFor(svc, 'wave'), due: waveAt(svc.calendar(), calendarMarea(s), wave), now: () => svc.now(), names: Object.fromEntries(s.players.map((p) => [p.id, heroName(p)])), allies: alliesFor(svc), keep: keepChatFor(svc) },
    onExit: () => {
      link.close();
      bastion(svc);
    },
    onEnd: () => {
      link.close();
      afterFight(screen, svc.refresh(), () => bastion(svc));
    },
  });
  show(screen.el, screen);
  screen.start();
};

type ReplayData = { params: Parameters<typeof createBattle>[0]; inputs: { tick: number; cmds: never[] }[]; pid: string | null; bundles?: { tick: number; inputs: Record<string, never[]> }[] };

/** `slot`: the calendar slot the replay is stored under (the Marea's index unless it was played again). */
const replay = async (svc: ServerCampaign, slot: number, wave: number, sector: number): Promise<void> => {
  let data: ReplayData;
  try {
    data = await api(`/api/campaigns/${svc.id}/replays/${slot}-${wave}-${sector}`);
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  playReplay(data, data.params.marea ?? slot, wave, svc.me, () => bastion(svc));
};

const playReplay = (data: ReplayData, marea: number, wave: number, me: string, back: () => void): void => {
  const r = getRenderer();
  if (!r) return noGl();
  const screen: BattleScreen = new BattleScreen({
    // Set up as the live room set it up (ghosts until their players joined, the lobby before the wave's hour).
    state: replayStart(data.params, data.bundles),
    me,
    renderer: r,
    sandbox: false,
    allowPause: true,
    title: t('bastion.replayTitle', { w: wave + 1, m: marea + 1 }),
    subtitle: t('hud.marea', { n: marea + 1 }),
    replay: { pid: data.pid, inputs: data.inputs, ...(data.bundles ? { bundles: data.bundles } : {}) },
    onExit: back,
    onEnd: () => screen.settle(back),
  });
  show(screen.el, screen);
  screen.start();
};

// ---------- practice ----------

const practice = (seed = (Date.now() >>> 0) % 100000, demo: PracticeDemo = {}): void => {
  const r = getRenderer();
  if (!r) return noGl();
  const state = createPractice(seed, t(`class.${demo.cls ?? 'warrior'}`), demo);
  const thumbs: Record<string, string> = {};
  for (const type of PRACTICE_BUILDS) thumbs[type] = thumb(r, `struct:${type}:1:1`);
  const screen: BattleScreen = new BattleScreen({
    state,
    me: 'me',
    renderer: r,
    sandbox: true,
    allowPause: true,
    title: t('menu.practice'),
    buildable: PRACTICE_BUILDS,
    coach: !tutorialDone(),
    thumbs,
    onExit: () => void menu(),
    onEnd: (s) => screen.settle(() => endModal(s, () => practice(seed + 1))),
  });
  show(screen.el, screen);
  screen.start();
};

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

/**
 * The bastion from scratch, played locally (`?bastion=1`, docs/CONSTRUCCION.md): terraced land, the keep
 * alone, walls drawn by dragging and three Tides one after another. `&seed=` picks other land, `&cls=` another hero.
 */
const landPoc = (p: LandPoc): void => {
  const r = getRenderer();
  if (!r) return noGl();
  const state = landBattle(p, t(`class.${p.cls}`));
  const thumbs: Record<string, string> = {};
  for (const type of LAND_BUILDS) thumbs[type] = thumb(r, `struct:${type}:1:1`);
  const sub = t('land.sub', { n: ROMAN[p.marea] ?? String(p.marea + 1) });
  const panel = h('div.land-hud', { 'data-test': 'land-hud' });
  // On a phone the panel starts folded, so it doesn't cover the board.
  const folded = window.innerWidth <= 640;
  if (folded) panel.classList.add('closed');
  const toggle = h('button.min', { onclick: () => {
    panel.classList.toggle('closed');
    toggle.textContent = panel.classList.contains('closed') ? t('land.showPanel') : t('land.hide');
  } }, folded ? t('land.showPanel') : t('land.hide'));
  panel.append(toggle, h('h3', {}, t('land.title')), h('p', {}, sub), h('p', {}, t('land.hint')));
  const screen: BattleScreen = new BattleScreen({
    state,
    me: 'me',
    renderer: r,
    sandbox: true,
    allowPause: true,
    title: t('land.title'),
    subtitle: sub,
    buildable: LAND_BUILDS,
    thumbs,
    land: { hud: panel },
    onExit: () => void menu(),
    onEnd: (s) => screen.settle(() => landEnd(s, p)),
  });
  show(screen.el, screen);
  screen.start();
};

const landEnd = (s: BattleState, p: LandPoc): HTMLElement => {
  const last = p.marea >= LAND_MAREAS - 1;
  const again = (): void => {
    afterLandTide(p, s, false);
    landPoc(p);
  };
  const next = (): void => {
    afterLandTide(p, s, true);
    landPoc(p);
  };
  const card = endModal(s, again);
  if (last) card.querySelector('.body')?.append(h('p', {}, t('land.last')));
  const foot = card.querySelector('footer');
  if (foot) {
    clear(foot);
    foot.append(h('button.btn.ghost', { onclick: () => landPoc(createLandPoc(p.seed + 1, p.cls)) }, t('land.reset')), h('button.btn.ghost', { onclick: () => again() }, t('land.again')));
    if (!last) foot.append(h('button.btn', { 'data-test': 'next-tide', onclick: () => next() }, t('land.next')));
  }
  return card;
};

const endModal = (s: BattleState, retry: () => void): HTMLElement => {
  const won = s.result === 'won';
  const me = s.stats.players.me;
  const back = h('div.modal-back');
  back.append(
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true', 'data-test': won ? 'won' : 'lost' },
      h('header', {}, h('h2', {}, won ? t('end.won') : t('end.lost')), h('p', {}, won ? t('end.wonSub') : t('end.lostSub'))),
      h(
        'div.body',
        {},
        h(
          'dl.stats',
          {},
          h('dt', {}, t('end.kills')),
          h('dd', {}, String(s.stats.kills)),
          h('dt', {}, t('end.damage')),
          h('dd', {}, String(me?.damage ?? 0)),
          h('dt', {}, t('end.keep')),
          h('dd', {}, `${Math.max(0, s.keep.hp)} / ${s.keep.maxHp}`),
          h('dt', {}, t('end.time')),
          h('dd', {}, `${Math.floor(s.waveT / 720)}:${String(Math.floor(s.waveT / 12) % 60).padStart(2, '0')}`),
        ),
      ),
      h('footer', {}, h('button.btn.ghost', { onclick: () => void menu() }, t('end.menu')), h('button.btn', { onclick: () => retry() }, t('end.retry'))),
    ),
  );
  return back;
};

const boot = async (): Promise<void> => {
  const params = new URLSearchParams(location.search);
  // ?saga=1: shows the saga before it opens for everyone (local campaigns play it; the server still refuses it).
  if (params.get('saga') === '1') SAGA_SWITCH.on = true;
  if (params.has('practice')) {
    // Demo scenarios exist only in development builds (recording, sound checks).
    const demo: PracticeDemo = import.meta.env.DEV
      ? { ...(['boss', 'mix', 'era1', 'era2', 'era3', 'era4', 'era5', 'forms'].includes(params.get('demo') ?? '') ? { demo: params.get('demo') as 'boss' | 'mix' | 'era1' | 'era2' | 'era3' | 'era4' | 'era5' | 'forms' } : {}), ...(HERO_CLASSES.includes(params.get('cls') as HeroClass) ? { cls: params.get('cls') as HeroClass } : {}), ...(params.get('omen') && params.get('omen') as string in INCURSION_OMENS ? { omen: params.get('omen') as IncursionOmen } : {}), ...(params.get('sky') === 'fog' || params.get('sky') === 'storm' || params.get('sky') === 'frost' ? { sky: params.get('sky') as 'fog' | 'storm' | 'frost' } : {}), ...(Number(params.get('awaken')) > 0 ? { awaken: Math.min(4, Number(params.get('awaken'))) } : {}), ...(params.get('forms') === 'a' || params.get('forms') === 'b' ? { forms: params.get('forms') as 'a' | 'b' } : {}), ...(params.get('party') ? { party: (params.get('party') as string).split(',').filter((c): c is HeroClass => HERO_CLASSES.includes(c as HeroClass)).slice(0, 7) } : {}) }
      : {};
    return practice(Number(params.get('seed') ?? 7), demo);
  }
  if (params.has('bastion')) {
    const cls = HERO_CLASSES.includes(params.get('cls') as HeroClass) ? (params.get('cls') as HeroClass) : 'warrior';
    return landPoc(createLandPoc(Number(params.get('seed') ?? 7), cls));
  }
  if (params.get('local')) return openLocal(params.get('local') as string);
  // A replay from a file (development only): a real wave to look at or to measure frame rates with.
  if (import.meta.env.DEV && params.get('replay')) {
    const data = (await (await fetch(params.get('replay') as string)).json()) as ReplayData;
    return playReplay(data, data.params.marea ?? 0, data.params.wave ?? 0, data.pid ?? '', () => (location.href = '/'));
  }
  // The service worker (wave notifications) — only on the real site, not in dev or tests.
  if (!import.meta.env.DEV) void registerWorker();
  await menu();
  if (params.get('auth') === 'invalid') toastGlobal(t('auth.invalid'));
  const invite = params.get('invite');
  if (invite) {
    if (!me) signInModal(`/?invite=${invite}`);
    else void acceptInviteModal(getRenderer(), me, invite, (id) => void openServer(id));
  }
  const c = params.get('c');
  if (c && me) void openServer(c);
  // ?cine=<id>: plays a cinematic right away (to share it or check it).
  const cine = CINEMATICS.find((x) => x.id === params.get('cine'));
  if (cine) playCinematic(cine);
};
watchForUpdates();
watchErrors();
void boot();
